// components/list/factoryFeedbackLearning.ts
export type FactoryFeedbackVote = 'good' | 'bad';

export interface FactoryFeedbackEntry {
  versionId: string;
  vote: FactoryFeedbackVote;
  createdAt: number;
  /** Stable server ordering for equal timestamps; omitted for legacy browser-only entries. */
  sourceKey?: string;
}

type FactoryFeedbackSnapshot = {
  id?: string | null;
  projectId?: string | null;
  candidateVersionId?: string | null;
  acceptedVersionId?: string | null;
  layerId?: string | null;
  isAccepted?: boolean | null;
  annotations?: unknown[] | null;
  params?: unknown;
  finalAssetUrls?: unknown;
  finalMetadata?: unknown;
  finalizedAt?: unknown;
  createdAt?: unknown;
};

export type FactoryApprovedOutput = {
  id: string;
  name: string;
  url: string;
  createdAt: number;
};

export type FactoryFeedbackProjection = {
  entries: FactoryFeedbackEntry[];
  approvedOutputs: FactoryApprovedOutput[];
};

const STORAGE_PREFIX = 'fater:factory-feedback:';
const SERVER_ANNOTATION_KIND = 'factory-feedback';
const MAX_EXAMPLES_PER_VOTE = 3;

const isBrowser = () => typeof window !== 'undefined' && Boolean(window.localStorage);

export const isFactoryOutputParams = (params: Record<string, unknown> | null | undefined): boolean => {
  if (!params) return false;
  if (params.factory === true) return true;
  if (params.factoryFeedbackLearning === true) return true;
  if (typeof params.factoryRunId === 'string' && params.factoryRunId.trim().length > 0) return true;
  return typeof params.prompt === 'string'
    && params.prompt.includes('URW/Westfield image-bank caliber')
    && params.prompt.includes('This is output');
};

const storageKey = (projectId: string) => `${STORAGE_PREFIX}${projectId}`;

const normalizePrompt = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.replace(/\s+/g, ' ').trim();
  return trimmed.length > 0 ? trimmed.slice(0, 260) : null;
};

const normalizeTimestamp = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
};

const getProjectOutputLayerIds = (snapshot: FactoryFeedbackSnapshot): string[] => {
  const metadata = snapshot.finalMetadata as { projectOutput?: { layerIds?: unknown } } | null;
  const layerIds = metadata?.projectOutput?.layerIds;
  return Array.isArray(layerIds) ? layerIds.map((id) => typeof id === 'string' ? id.trim() : '') : [];
};

const dedupeFeedbackEntries = (entries: FactoryFeedbackEntry[]): FactoryFeedbackEntry[] => {
  const seen = new Set<string>();
  const result: FactoryFeedbackEntry[] = [];
  for (const entry of [...entries].sort((a, b) => (
    b.createdAt - a.createdAt
    || (b.sourceKey ?? '').localeCompare(a.sourceKey ?? '')
    || a.versionId.localeCompare(b.versionId)
  ))) {
    if (seen.has(entry.versionId)) continue;
    seen.add(entry.versionId);
    result.push(entry);
  }
  return result;
};

export const readFactoryFeedbackEntries = (projectId: string): FactoryFeedbackEntry[] => {
  if (!projectId || !isBrowser()) return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(storageKey(projectId)) ?? '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((entry): entry is FactoryFeedbackEntry => (
      entry &&
      typeof entry.versionId === 'string' &&
      (entry.vote === 'good' || entry.vote === 'bad') &&
      typeof entry.createdAt === 'number'
    ));
  } catch {
    return [];
  }
};

export const readFactoryFeedbackEntriesFromSnapshots = (
  snapshots: FactoryFeedbackSnapshot[] | null | undefined,
  projectId?: string,
): FactoryFeedbackEntry[] => {
  if (!Array.isArray(snapshots)) return [];
  const entries: FactoryFeedbackEntry[] = [];
  for (const [snapshotIndex, snapshot] of snapshots.entries()) {
    if (projectId && snapshot.projectId !== projectId) continue;
    const annotations = snapshot.annotations ?? [];
    const hasFactoryAnnotation = annotations.some((annotation) => (
      annotation && typeof annotation === 'object' && (annotation as Record<string, unknown>).kind === SERVER_ANNOTATION_KIND
    ));
    const params = snapshot.params && typeof snapshot.params === 'object'
      ? snapshot.params as Record<string, unknown>
      : null;
    if (!hasFactoryAnnotation && !isFactoryOutputParams(params)) continue;
    const fallbackVersionId =
      typeof snapshot.candidateVersionId === 'string' && snapshot.candidateVersionId.trim().length > 0
        ? snapshot.candidateVersionId.trim()
        : null;
    const lineageIds = new Set([
      typeof snapshot.candidateVersionId === 'string' ? snapshot.candidateVersionId.trim() : '',
      typeof snapshot.acceptedVersionId === 'string' ? snapshot.acceptedVersionId.trim() : '',
      typeof snapshot.layerId === 'string' ? snapshot.layerId.trim() : '',
      ...getProjectOutputLayerIds(snapshot),
    ].filter(Boolean));
    for (const [annotationIndex, annotation] of annotations.entries()) {
      if (!annotation || typeof annotation !== 'object') continue;
      const record = annotation as Record<string, unknown>;
      if (record.kind !== SERVER_ANNOTATION_KIND) continue;
      const vote = record.value === 'good' || record.value === 'bad' ? record.value : null;
      if (!vote) continue;
      const versionId = normalizePrompt(record.versionId) ?? fallbackVersionId;
      if (!versionId || !lineageIds.has(versionId)) continue;
      entries.push({
        versionId,
        vote,
        createdAt: normalizeTimestamp(record.evaluatedAt) || normalizeTimestamp(snapshot.createdAt),
        sourceKey: `${snapshot.id ?? snapshotIndex}:${String(annotationIndex).padStart(6, '0')}`,
      });
    }

    if (snapshot.isAccepted === true) {
      const versionId =
        typeof snapshot.acceptedVersionId === 'string' && snapshot.acceptedVersionId.trim().length > 0
          ? snapshot.acceptedVersionId.trim()
          : fallbackVersionId;
      if (versionId) {
        entries.push({
          versionId,
          vote: 'good',
          createdAt: 0,
          sourceKey: `${snapshot.id ?? snapshotIndex}:legacy`,
        });
      }
    }
  }
  return dedupeFeedbackEntries(entries);
};

const isHttpsUrl = (value: string): boolean => {
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
};

const isFactoryAssetUrl = (value: string): boolean => (
  value.startsWith('/api/assets/')
  || value.startsWith('/api/user-files/')
  || isHttpsUrl(value)
);

const isFactoryImageAssetUrl = (value: string): boolean => (
  isFactoryAssetUrl(value) && !/\.(?:mp4|mov|webm|m4v|mkv)(?:[?#]|$)/i.test(value)
);

export const readFactoryApprovedOutputsFromSnapshots = (
  snapshots: FactoryFeedbackSnapshot[] | null | undefined,
  projectId: string,
): FactoryApprovedOutput[] => {
  if (!projectId || !Array.isArray(snapshots)) return [];
  const entries = readFactoryFeedbackEntriesFromSnapshots(snapshots, projectId);
  const latestVotes = new Map(entries.map((entry) => [entry.versionId, entry.vote]));
  const media = new Map<string, FactoryApprovedOutput>();
  for (const snapshot of snapshots) {
    if (snapshot.projectId !== projectId) continue;
    const versionIds = [snapshot.acceptedVersionId, snapshot.candidateVersionId, snapshot.layerId]
      .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
      .map((value) => value.trim());
    const fallbackUrl = Array.isArray(snapshot.finalAssetUrls)
      ? snapshot.finalAssetUrls.find((value): value is string => typeof value === 'string' && isFactoryImageAssetUrl(value.trim()))?.trim()
      : undefined;
    const urlsByOutput = new Map(versionIds.map((id) => [id, fallbackUrl]));
    for (const [index, layerId] of getProjectOutputLayerIds(snapshot).entries()) {
      if (!layerId) continue;
      const asset = Array.isArray(snapshot.finalAssetUrls) ? snapshot.finalAssetUrls[index] : undefined;
      urlsByOutput.set(layerId, typeof asset === 'string' && isFactoryImageAssetUrl(asset.trim()) ? asset.trim() : undefined);
    }
    const createdAt = normalizeTimestamp(snapshot.finalizedAt ?? snapshot.createdAt);
    for (const [versionId, url] of urlsByOutput) {
      if (!url || latestVotes.get(versionId) !== 'good') continue;
      const candidate = { id: versionId, name: 'Validated output', url, createdAt };
      const existing = media.get(versionId);
      if (!existing || candidate.createdAt > existing.createdAt) media.set(versionId, candidate);
    }
  }
  return [...media.values()].sort((left, right) => (
    right.createdAt - left.createdAt || left.id.localeCompare(right.id)
  ));
};

export const hydrateFactoryFeedbackEntries = (
  projectId: string,
  entries: FactoryFeedbackEntry[],
): FactoryFeedbackEntry[] => {
  if (!projectId || !isBrowser() || entries.length === 0) return readFactoryFeedbackEntries(projectId);
  const next = dedupeFeedbackEntries([...entries, ...readFactoryFeedbackEntries(projectId)]);
  window.localStorage.setItem(storageKey(projectId), JSON.stringify(next));
  return next;
};

export const loadFactoryFeedbackProjection = async (projectId: string): Promise<FactoryFeedbackProjection> => {
  if (!projectId || !isBrowser()) return { entries: [], approvedOutputs: [] };
  try {
    const res = await fetch(`/api/inventory/projects/${encodeURIComponent(projectId)}/snapshots`);
    if (!res.ok) return { entries: readFactoryFeedbackEntries(projectId), approvedOutputs: [] };
    const data = await res.json();
    const snapshots = Array.isArray(data?.snapshots) ? data.snapshots as FactoryFeedbackSnapshot[] : [];
    const serverEntries = readFactoryFeedbackEntriesFromSnapshots(snapshots, projectId);
    return {
      entries: hydrateFactoryFeedbackEntries(projectId, serverEntries),
      approvedOutputs: readFactoryApprovedOutputsFromSnapshots(snapshots, projectId),
    };
  } catch {
    return { entries: readFactoryFeedbackEntries(projectId), approvedOutputs: [] };
  }
};

export const loadFactoryFeedbackEntries = async (projectId: string): Promise<FactoryFeedbackEntry[]> => (
  await loadFactoryFeedbackProjection(projectId)
).entries;

export const writeFactoryFeedbackEntry = (
  projectId: string,
  versionId: string,
  vote: FactoryFeedbackVote,
): FactoryFeedbackEntry[] => {
  if (!projectId || !versionId || !isBrowser()) return [];
  const next = [
    { versionId, vote, createdAt: Date.now() },
    ...readFactoryFeedbackEntries(projectId).filter((entry) => entry.versionId !== versionId),
  ];
  window.localStorage.setItem(storageKey(projectId), JSON.stringify(next));
  return next;
};

export const getFactoryFeedbackVote = (projectId: string, versionId: string): FactoryFeedbackVote | null =>
  readFactoryFeedbackEntries(projectId).find((entry) => entry.versionId === versionId)?.vote ?? null;

export const restoreFactoryFeedbackEntry = (
  projectId: string,
  versionId: string,
  vote: FactoryFeedbackVote | null | undefined,
): FactoryFeedbackEntry[] => {
  if (vote === 'good' || vote === 'bad') return writeFactoryFeedbackEntry(projectId, versionId, vote);
  if (!projectId || !versionId || !isBrowser()) return [];
  const next = readFactoryFeedbackEntries(projectId).filter((entry) => entry.versionId !== versionId);
  window.localStorage.setItem(storageKey(projectId), JSON.stringify(next));
  return next;
};

export const buildFactoryFeedbackPrompt = (projectId: string, extraEntries: FactoryFeedbackEntry[] = []): string => {
  const entries = dedupeFeedbackEntries([...extraEntries, ...readFactoryFeedbackEntries(projectId)]);
  const good = entries.filter((entry) => entry.vote === 'good').slice(0, MAX_EXAMPLES_PER_VOTE);
  const bad = entries.filter((entry) => entry.vote === 'bad').slice(0, MAX_EXAMPLES_PER_VOTE);
  const lines = [
    '[Factory style learning] Derive the scene from the observed brand imagery: gesture, situation, camera, framing, colour and light. Adapt those cues into an original image; do not copy a source image wholesale.',
    'The user brief and selected archetypes control requested content. Selected personas alone define identity; selected Objects define product appearance. Brand examples cannot replace them or require incidental people, outfits or products.',
  ];
  if (good.length > 0) lines.push(`Keep the abstract visual quality signalled by ${good.length} approved result${good.length === 1 ? '' : 's'}; never reuse their person, scene, or composition.`);
  if (bad.length > 0) lines.push(`Avoid the generic quality signalled by ${bad.length} rejected result${bad.length === 1 ? '' : 's'}; never reuse their person, scene, or composition.`);
  return `${lines.join(' ')} `;
};

export const withFactoryFeedbackPrompt = (
  projectId: string,
  prompt: string,
  extraEntries: FactoryFeedbackEntry[] = [],
): string => {
  const normalizedPrompt = typeof prompt === 'string' ? prompt.trim() : '';
  if (!normalizedPrompt) return buildFactoryFeedbackPrompt(projectId, extraEntries).trim();
  if (normalizedPrompt.includes('[Factory style learning]')) return normalizedPrompt;
  return `${buildFactoryFeedbackPrompt(projectId, extraEntries)}${normalizedPrompt}`;
};
